import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Collapse, Stack } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppContext from "../context/app-context";
import { getExpenseFieldsFromProfile, getLocalStorageItem } from "../utils/helperFunctions";
import CustomTable from "./customTable";
import i18n from "../i18n";
import { EditableModal } from "./editableModal";
import { INPUT_TYPE } from "../constants";
import { da } from "date-fns/locale";
import { utils, writeFile } from "xlsx";
import { format } from "date-fns";
import { toast } from "react-toastify";

export default function ExpenseList(props) {
  const { items = [], defaultOpen = true, paginationSize = 20, loadItems = () => {}, onlineItems = [] } = props;
  const [open, setOpen] = useState(defaultOpen);
  const { userProfile, setLoading } = useContext(AppContext);
  const navigate = useNavigate();
  const { fieldConfigs = [] } = userProfile || {};
  const { t } = useTranslation();

  const fieldShowInDashboard = fieldConfigs.filter((item) => item.showInDashboard);
  const [customCategory, setCustomCategory] = useState([]);
  useEffect(() => {
    const { customCategory: c2 = [] } = getLocalStorageItem("customCategory") || {};
    setCustomCategory(c2);
  }, []);

  const fieldConfigsForDownload = getExpenseFieldsFromProfile(userProfile);
  const handleExport = (selectedFields) => {
    try {
      setLoading(true);
      const selectedConfigs = fieldConfigsForDownload
        .filter((config) => selectedFields[config.fieldName] === true)
        .map((config) => {
          const { label } = config;
          const labelKey = `EditableModal.fields.${label}`;
          return {
            ...config,
            label: t(labelKey, label),
          };
        });

      // Prepare data
      const data = items.map((item) => {
        const row = {};
        selectedConfigs.forEach((config) => {
          const value = item[config.fieldName];
          if (config.inputType === INPUT_TYPE.DATE && value) {
            row[config.label] = format(new Date(value), "yyyy-MM-dd");
          } else {
            row[config.label] = value || "";
          }
        });
        return row;
      });

      const worksheet = utils.json_to_sheet(data);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Expenses");

      // Write file
      writeFile(workbook, `expenses_export_${new Date().toLocaleDateString()}.xlsx`, { compression: true });
      toast.success(t("ExpenseList.exportSuccess"));
    } catch (error) {
      console.error("Export failed:", error);
      toast.error(t("ExpenseList.exportError"));
    }
    setLoading(false);
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <Stack direction="horizontal" gap={3} className="mb-3 flex-wrap">
        <Button
          onClick={() => setOpen(!open)}
          aria-controls="expense-table"
          aria-expanded={open}
          variant="outline-primary"
          className="me-md-2"
          style={{
            borderRadius: "15px",
            border: "2px solid #6EC3C4",
            backgroundColor: open ? "#6EC3C422" : "white",
            fontWeight: 600,
            flex: "1 0 auto",
            minWidth: "200px",
          }}
        >
          {open ? t("ExpenseList.hide") : t("ExpenseList.show")}
        </Button>

        <EditableModal
          title={t("ExpenseList.exportTitle")}
          configs={fieldConfigsForDownload.map((config) => ({
            ...config,
            inputType: INPUT_TYPE.BOOLEAN,
            required: false,
            label: t(`EditableModal.fields.${config.label}`, config.label),
          }))}
          actionLabel={t("ExpenseList.exportButton")}
          actionOnClick={handleExport}
          buttonStyle={{
            background: "linear-gradient(135deg, #FF7F7F 0%, #6EC3C4 100%)",
            border: "none",
            borderRadius: "15px",
            padding: "0.5rem 1.5rem",
            flex: "1 0 auto",
            minWidth: "200px",
          }}
        />
      </Stack>

      <Collapse in={open}>
        <div id="expense-table" className="mt-3">
          <Card className="border-0">
            <Card.Body style={{ backgroundColor: "#f8f9fa", borderRadius: "15px" }}>
              <CustomTable
                headers={["title", "total", "purchasedDate", ...fieldShowInDashboard.map((i) => i.label)].map((label) => {
                  const labelKey = `EditableModal.fields.${label}`;
                  const translatedLabel = i18n.exists(labelKey) ? t(labelKey) : label;
                  return translatedLabel;
                })}
                colCanSort={[true, true, true, true, true]}
                paginationSize={paginationSize}
                rows={items.map((item) => {
                  return [
                    { value: item.title },
                    { value: `$ ${item.total || 0}` },
                    { value: item.purchasedDate },
                    ...fieldShowInDashboard.map((att) => {
                      const keyName = Object.keys(item).find((k) => k.toLowerCase() === att.fieldName.toLowerCase());
                      if (keyName) {
                        const fieldValue = item[keyName];
                        const labelKey = `EditableModal.selectValues.${fieldValue}`;
                        const translatedLabel = i18n.exists(labelKey) ? t(labelKey) : fieldValue;
                        return { value: translatedLabel };
                      }
                      return { value: "" };
                    }),
                  ].map((val) => ({ ...val, onClick: () => navigate(`/details/${item.expenseId}`) }));
                })}
                headerStyle={{
                  backgroundColor: "#6EC3C4",
                  color: "white",
                  borderRadius: "10px",
                }}
              />
            </Card.Body>
          </Card>
        </div>
      </Collapse>
    </div>
  );
}
